import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart, Loader2, Trash2, User, ShoppingBag, MessageSquare,
  Store, PlayCircle, MapPin, GraduationCap, Briefcase,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

/**
 * Universal Saved — single home for every "Save" the user has done
 * across the app. Merges the old Wishlist tabs (people / items / posts)
 * with the four new target types added in Slice 5a:
 *   · listing (services & products)
 *   · video   (reels / video intros)
 *   · place   (POIs / user-tagged pins)
 *   · school  (schools search bookmarks)
 *   · job     (jobs awareness picks — placeholder card until Slice 7)
 *
 * All Save/Unsave actions across the app funnel through
 * `POST /api/favorites/toggle` — there is NO alternative endpoint. The
 * legacy `POST /api/personal/favorites` is deprecated (module-launcher only).
 *
 * Available at both `/wishlist` (legacy URL, still works) and `/saved` (new).
 */
const TABS = [
  { k: "all",          l: "All",       Icon: Heart          },
  { k: "listing",      l: "Services",  Icon: Store          },
  { k: "catalog_item", l: "Items",     Icon: ShoppingBag    },
  { k: "video",        l: "Videos",    Icon: PlayCircle     },
  { k: "place",        l: "Places",    Icon: MapPin         },
  { k: "school",       l: "Schools",   Icon: GraduationCap  },
  { k: "job",          l: "Jobs",      Icon: Briefcase      },
  { k: "user",         l: "People",    Icon: User           },
  { k: "post",         l: "Posts",     Icon: MessageSquare  },
];

export default function Wishlist() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const q = tab === "all" ? "" : `?target_type=${tab}`;
      const r = await api.get(`/favorites${q}`);
      setItems(r.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (user) load(); }, [user, tab]);   // eslint-disable-line

  const removeFav = async (f) => {
    try {
      await api.post("/favorites/toggle", { target_type: f.target_type, target_id: f.target_id });
      setItems(items.filter(x => !(x.target_type === f.target_type && x.target_id === f.target_id)));
      toast.success("Removed");
    } catch { toast.error("Failed"); }
  };

  if (authLoading) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;
  if (!user) return (
    <div className="p-12 text-center text-stone-500" data-testid="saved-signin-gate">
      Sign in to see everything you've saved.
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-4" data-testid="saved-page">
      <div className="flex items-center gap-2 mb-1">
        <Heart className="text-rose-500" fill="currentColor" size={24} />
        <h1 className="heading text-2xl font-black text-stone-900">Saved</h1>
      </div>
      <p className="text-xs text-stone-500 mb-4">
        Everything you've saved — services, items, reels, places, schools, jobs, people, posts.
      </p>

      <div className="flex gap-1 mb-4 overflow-x-auto -mx-4 px-4 pb-1">
        {TABS.map(({ k, l, Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full shrink-0 ${
              tab === k
                ? "bg-stone-900 text-white"
                : "bg-stone-100 text-stone-600 hover:bg-stone-200"
            }`}
            data-testid={`wishlist-tab-${k}`}
          >
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="inline animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-stone-500" data-testid="saved-empty">
          <Heart className="mx-auto mb-2 text-stone-300" size={32} />
          <div className="font-bold">Nothing saved yet</div>
          <div className="text-xs mt-1">
            Tap the ♥ on any listing, reel, place, school, item or profile to add it here.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map(f => (
            <div
              key={`${f.target_type}-${f.target_id}`}
              className="bg-white border border-stone-200 rounded-2xl p-4 flex items-center gap-3"
              data-testid={`wishlist-${f.target_type}-${f.target_id}`}
            >
              <FavCard f={f} />
              <button
                onClick={() => removeFav(f)}
                className="text-red-500 p-2 rounded-full hover:bg-red-50 shrink-0"
                data-testid={`wishlist-remove-${f.target_id}`}
                aria-label="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- one card per target type ---------- */
function FavCard({ f }) {
  const c = f.card;

  if (f.target_type === "user" && c) {
    return (
      <Link to={`/u/${c.nvn || c.user_id}`} className="flex-1 flex items-center gap-3 min-w-0">
        {c.picture
          ? <img loading="lazy" src={c.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
          : <div className="w-12 h-12 rounded-full bg-stone-300 grid place-items-center text-white font-bold"><User size={20} /></div>}
        <div className="min-w-0">
          <div className="font-bold text-stone-900 truncate">{c.name}</div>
          {c.nvn && <div className="font-mono text-[11px] text-stone-500">NVN {c.nvn}</div>}
        </div>
      </Link>
    );
  }

  if (f.target_type === "catalog_item" && c) {
    return (
      <Link to={`/store/${c.seller_nvn || c.seller_id}`} className="flex-1 flex items-center gap-3 min-w-0">
        {c.image
          ? <img loading="lazy" src={c.image} alt="" className="w-12 h-12 rounded-xl object-cover" />
          : <div className="w-12 h-12 rounded-xl bg-stone-200 grid place-items-center text-stone-400"><ShoppingBag size={18} /></div>}
        <div className="min-w-0">
          <div className="font-bold text-stone-900 truncate">{c.name}</div>
          <div className="text-sm text-[#E05E36] font-black">{rupees(c.price_paise)}</div>
        </div>
      </Link>
    );
  }

  if (f.target_type === "post" && c) {
    return (
      <Link to="/feed" className="flex-1 flex items-center gap-3 min-w-0">
        <div className="w-12 h-12 rounded-xl bg-stone-100 grid place-items-center text-stone-400"><MessageSquare size={18} /></div>
        <div className="min-w-0">
          <div className="font-bold text-stone-900 truncate text-sm">Saved post</div>
          <div className="text-xs text-stone-500 line-clamp-1">{c.body || "Attachment"}</div>
        </div>
      </Link>
    );
  }

  if (f.target_type === "listing" && c) {
    return (
      <Link to={`/listing/${c.listing_id}`} className="flex-1 flex items-center gap-3 min-w-0">
        {c.images?.[0]
          ? <img loading="lazy" src={c.images[0]} alt="" className="w-12 h-12 rounded-xl object-cover" />
          : <div className="w-12 h-12 rounded-xl bg-stone-200 grid place-items-center text-stone-400"><Store size={18} /></div>}
        <div className="min-w-0">
          <div className="font-bold text-stone-900 truncate">{c.title}</div>
          <div className="text-[11px] text-stone-500 uppercase tracking-wider">
            {c.category}{c.city ? ` · ${c.city}` : ""}
            {c.rating_avg ? ` · ${c.rating_avg}★` : ""}
          </div>
        </div>
      </Link>
    );
  }

  if (f.target_type === "video" && c) {
    return (
      <Link to={`/reels?v=${c.video_id}`} className="flex-1 flex items-center gap-3 min-w-0">
        {c.thumbnail
          ? <img loading="lazy" src={c.thumbnail} alt="" className="w-12 h-12 rounded-xl object-cover" />
          : <div className="w-12 h-12 rounded-xl bg-stone-900 grid place-items-center text-white"><PlayCircle size={18} /></div>}
        <div className="min-w-0">
          <div className="font-bold text-stone-900 truncate">{c.title || "Saved reel"}</div>
          <div className="text-[11px] text-stone-500">
            {c.creator_type || "creator"}{c.provider ? ` · ${c.provider}` : ""}
          </div>
        </div>
      </Link>
    );
  }

  if (f.target_type === "place" && c) {
    return (
      <Link to={`/map?place=${c.place_id}`} className="flex-1 flex items-center gap-3 min-w-0">
        <div className="w-12 h-12 rounded-xl bg-sky-100 text-sky-700 grid place-items-center"><MapPin size={18} /></div>
        <div className="min-w-0">
          <div className="font-bold text-stone-900 truncate">{c.name || c.category}</div>
          <div className="text-[11px] text-stone-500 uppercase">
            {c.category} · {c.verification_status || "unverified"}
          </div>
        </div>
      </Link>
    );
  }

  if (f.target_type === "school" && c) {
    return (
      <Link to={`/schools?code=${c.code}`} className="flex-1 flex items-center gap-3 min-w-0">
        <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><GraduationCap size={18} /></div>
        <div className="min-w-0">
          <div className="font-bold text-stone-900 truncate">{c.name}</div>
          <div className="text-[11px] text-stone-500">
            {c.code}{c.city ? ` · ${c.city}` : ""}
          </div>
        </div>
      </Link>
    );
  }

  if (f.target_type === "job") {
    return (
      <Link to="/jobs" className="flex-1 flex items-center gap-3 min-w-0">
        <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 grid place-items-center"><Briefcase size={18} /></div>
        <div className="min-w-0">
          <div className="font-bold text-stone-900 truncate">Saved job</div>
          <div className="text-[11px] text-stone-500 truncate">{f.target_id}</div>
        </div>
      </Link>
    );
  }

  return <div className="flex-1 text-sm text-stone-500">Unavailable</div>;
}
